import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, Tooltip } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

import { SearchGroupsQueryVariables, useBatchDeleteGroupsMutation } from 'api';
import { UseCheckedState } from 'types/app';
import { Alert } from 'components';
import { useNotify } from 'hooks';
import { generateLink } from 'Links';

import { useBatchDeleteGroups } from 'features/admin/updaters';

interface Props extends Partial<UseCheckedState> {
  variables: SearchGroupsQueryVariables;
}

const Actions: FC<Props> = ({ variables, checkedIds, resetCheckedState }) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const batchDeleteMutationOptions = useBatchDeleteGroups(variables);
  const { mutate: batchDeleteAdminGroups } = useBatchDeleteGroupsMutation({
    onSuccess: handleMutationCompleted,
    ...batchDeleteMutationOptions,
  });

  const showEdit = checkedIds.length === 1;
  const showDelete = !!checkedIds.length;

  function handleMutationCompleted() {
    notify.info({
      i18n: {
        i18nKey: 'admin:deleteGroupSuccessMessage',
        count: checkedIds.length,
        values: { checkedTotal: checkedIds.length },
      },
    });
  }

  function handleCancel() {
    setOpen(false);
  }

  function handleBulkDeleteGroups() {
    batchDeleteAdminGroups({ ids: checkedIds });
    setOpen(false);
    resetCheckedState();
  }

  return (
    <Fragment>
      <Alert
        open={open}
        onConfirm={handleBulkDeleteGroups}
        onCancel={handleCancel}
        message={t('admin:deleteGroupMessage', { count: checkedIds.length })}
        confirm={t('common:delete')}
      />
      <Grid container alignItems="center" justify="flex-end">
        {showEdit && (
          <Grid item>
            <Tooltip title={t('common:edit')}>
              <IconButton component={generateLink(`/group/${checkedIds[0]}/edit`)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        {showDelete && (
          <Grid item>
            <Tooltip title={t('common:delete')}>
              <IconButton onClick={() => setOpen(true)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

export default Actions;
